import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { api, ApiError } from '../lib/api'
import type { User } from '../lib/types'

type AuthContextValue = {
  user: User | null
  loading: boolean
  authOpen: boolean
  authMode: 'wechat' | 'login' | 'register'
  openAuth: (mode?: 'wechat' | 'login' | 'register') => void
  closeAuth: () => void
  login: (username: string, password: string) => Promise<void>
  register: (username: string, password: string, email?: string) => Promise<void>
  logout: () => Promise<void>
  refresh: () => Promise<void>
  startWechatLogin: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [authOpen, setAuthOpen] = useState(false)
  const [authMode, setAuthMode] = useState<AuthContextValue['authMode']>('wechat')

  const refresh = useCallback(async () => {
    try {
      const url = new URL(window.location.href)
      const ticket = url.searchParams.get('wechat_ticket')
      const result = ticket
        ? await api<{ user?: User } | User>('/api/v1/auth/wechat/web/exchange', { method: 'POST', body: { ticket } })
        : await api<{ user?: User } | User>('/api/v1/auth/me')
      if (ticket) {
        url.searchParams.delete('wechat_ticket')
        url.searchParams.delete('state')
        history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`)
      }
      setUser('user' in result && result.user ? result.user : result as User)
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 401) console.warn(error)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void refresh() }, [refresh])

  const value = useMemo<AuthContextValue>(() => ({
    user, loading, authOpen, authMode,
    openAuth: (mode = 'wechat') => { setAuthMode(mode); setAuthOpen(true) },
    closeAuth: () => setAuthOpen(false),
    login: async (username, password) => {
      const result = await api<{ user?: User } | User>('/api/v1/auth/login', { method: 'POST', body: { username, password } })
      setUser('user' in result && result.user ? result.user : result as User); setAuthOpen(false)
    },
    register: async (username, password, email) => {
      const result = await api<{ user?: User } | User>('/api/v1/auth/register', { method: 'POST', body: { username, password, email: email || null } })
      setUser('user' in result && result.user ? result.user : result as User); setAuthOpen(false)
    },
    logout: async () => { await api('/api/v1/auth/logout', { method: 'POST' }); setUser(null) },
    refresh,
    startWechatLogin: () => {
      const returnTo = encodeURIComponent(window.location.href.split('#')[0])
      window.location.assign(`/api/v1/auth/wechat/web/start?return_to=${returnTo}`)
    },
  }), [user, loading, authOpen, authMode, refresh])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}
